/**
 * Builds a knockout bracket model from a league's schedule fixtures, by detecting
 * knockout-round fixtures (Round of 32 → Final, plus third place) from their raw
 * `round` label. Group-stage / regular-season fixtures are ignored.
 *
 * This keeps the Knockout tab gated: domestic single-table leagues have no
 * knockout rounds, so `hasLeagueKnockout` returns false and the tab stays hidden.
 */

import type {
  BracketMatch,
  BracketRound,
  BracketTeamSlot,
  KnockoutBracketModel,
} from '../../knockout/types/knockoutBracket.types'
import type { MatchStatus } from '../../worldcup/types/worldcup.types'
import type { LeagueFixture } from './leagueFixtureSections.helper'
import { MATCH_ROUTE_PREFIX, TBD_TEAM_LABEL } from '../constants/leagueDetail.constants'

interface KnockoutRoundDef {
  key: string
  label: string
  order: number
  test: RegExp
}

/** Third place must be checked before "Final" (the string "3rd Place Final" contains "final"). */
const THIRD_PLACE = /(3rd|third)[\s-]*place/i

const KNOCKOUT_ROUNDS: KnockoutRoundDef[] = [
  { key: 'round_of_128', label: 'Round of 128', order: 1, test: /round of 128|1\/64/i },
  { key: 'round_of_64', label: 'Round of 64', order: 2, test: /round of 64|1\/32/i },
  { key: 'round_of_32', label: 'Round of 32', order: 3, test: /round of 32|1\/16/i },
  { key: 'round_of_16', label: 'Round of 16', order: 4, test: /round of 16|1\/8|eighth[\s-]*final|8th[\s-]*final/i },
  { key: 'quarter_finals', label: 'Quarter-finals', order: 5, test: /quarter|1\/4|\bqf\b/i },
  { key: 'semi_finals', label: 'Semi-finals', order: 6, test: /semi|1\/2|\bsf\b/i },
  { key: 'final', label: 'Final', order: 7, test: /final/i },
]

type RoundClass = { def: KnockoutRoundDef; isThirdPlace: boolean } | null

function classifyRound(round: string | null | undefined): RoundClass {
  if (typeof round !== 'string') return null
  const trimmed = round.trim()
  if (trimmed === '') return null
  const isThirdPlace = THIRD_PLACE.test(trimmed)
  for (const def of KNOCKOUT_ROUNDS) {
    if (def.test.test(trimmed)) return { def, isThirdPlace }
  }
  // "3rd place" with no other round word still belongs in the bracket as the play-off.
  if (isThirdPlace) return { def: KNOCKOUT_ROUNDS[KNOCKOUT_ROUNDS.length - 1], isThirdPlace: true }
  return null
}

function mapStatus(status: LeagueFixture['status']): MatchStatus {
  if (status === 'live') return 'live'
  if (status === 'completed') return 'finished'
  return 'upcoming'
}

function formatDate(iso: string): string | null {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const weekday = new Intl.DateTimeFormat('en', { weekday: 'short' }).format(d)
  const day = new Intl.DateTimeFormat('en', { day: 'numeric' }).format(d)
  const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(d)
  return `${weekday} ${day} ${month}`
}

function formatTime(iso: string): string | null {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', hour12: false }).format(d)
}

function toSlot(
  name: string,
  kit: LeagueFixture['homeKit'],
  countryCode: string | undefined,
  score: number,
  played: boolean,
  isWinner: boolean | null,
): BracketTeamSlot {
  return {
    name: name && name !== '' ? name : null,
    placeholderLabel: name && name !== '' ? null : TBD_TEAM_LABEL,
    kit: kit ?? null,
    // National-team competitions carry a flag code; the bracket box prefers it over the kit.
    countryCode: countryCode && countryCode !== '' ? countryCode : null,
    score: played ? score : null,
    isWinner,
  }
}

function toBracketMatch(fx: LeagueFixture, isFinal: boolean): BracketMatch {
  const status = mapStatus(fx.status)
  const played = status === 'finished'
  let homeWin: boolean | null = null
  let awayWin: boolean | null = null
  if (played) {
    if (fx.homeScore > fx.awayScore) { homeWin = true; awayWin = false }
    else if (fx.awayScore > fx.homeScore) { homeWin = false; awayWin = true }
  }
  return {
    id: fx.id,
    home: toSlot(fx.homeTeam, fx.homeKit, fx.homeCountryCode, fx.homeScore, played, homeWin),
    away: toSlot(fx.awayTeam, fx.awayKit, fx.awayCountryCode, fx.awayScore, played, awayWin),
    dateLabel: formatDate(fx.date),
    timeLabel: formatTime(fx.date),
    status,
    isFinal,
    href: `${MATCH_ROUTE_PREFIX}${fx.id}`,
  }
}

function byDate(a: LeagueFixture, b: LeagueFixture): number {
  return new Date(a.date).getTime() - new Date(b.date).getTime()
}

export function buildLeagueKnockoutBracket(
  fixtures: readonly LeagueFixture[],
): KnockoutBracketModel {
  const buckets = new Map<string, { def: KnockoutRoundDef; matches: LeagueFixture[] }>()
  let thirdPlaceFixture: LeagueFixture | null = null

  for (const fx of fixtures) {
    const cls = classifyRound(fx.round)
    if (cls == null) continue
    if (cls.isThirdPlace) {
      if (thirdPlaceFixture == null) thirdPlaceFixture = fx
      continue
    }
    const existing = buckets.get(cls.def.key)
    if (existing != null) existing.matches.push(fx)
    else buckets.set(cls.def.key, { def: cls.def, matches: [fx] })
  }

  const rounds: BracketRound[] = Array.from(buckets.values())
    .sort((a, b) => a.def.order - b.def.order)
    .map(({ def, matches }) => {
      const isFinalRound = def.key === 'final'
      return {
        key: def.key,
        label: def.label,
        matches: matches
          .slice()
          .sort(byDate)
          .map((fx) => toBracketMatch(fx, isFinalRound)),
      }
    })

  return {
    rounds,
    thirdPlace: thirdPlaceFixture != null ? toBracketMatch(thirdPlaceFixture, false) : null,
  }
}

/** True when the loaded schedule contains at least one knockout-round fixture. */
export function hasLeagueKnockout(fixtures: readonly LeagueFixture[]): boolean {
  return fixtures.some((fx) => classifyRound(fx.round) != null)
}

/**
 * True for a national-team group tournament (e.g. the World Cup) reached via the league page:
 * some fixture carries a flag code AND the standings expose 2+ real letter groups (A–L). Used to
 * surface a provisional knockout bracket before the real knockout fixtures are drawn/synced.
 */
export function isNationalGroupTournament(
  fixtures: readonly LeagueFixture[],
  groupNames: readonly string[],
): boolean {
  const national = fixtures.some((fx) => fx.homeCountryCode != null && fx.homeCountryCode !== '')
  const letterGroups = groupNames.filter((name) => /group\s+[a-l]\b/i.test(name))
  return national && letterGroups.length > 1
}
